import { useState } from 'react';

const faqs = [
  {
    id: 'faq-1',
    question: 'What is a pandabuy spreadsheet?',
    answer: 'A pandabuy spreadsheet is a curated collection of product links organized by category, helping shoppers quickly find verified items from trusted sellers. Our spreadsheet covers sneakers, clothing, accessories, bags, and more — all updated regularly for 2026.',
  },
  {
    id: 'faq-2',
    question: 'How often is the pandabuy spreadsheet updated?',
    answer: 'Our pandabuy spreadsheet receives daily updates. We constantly check for dead links, add new product discoveries, and remove listings that no longer meet our quality standards. You will always find fresh, working links here.',
  },
  {
    id: 'faq-3',
    question: 'Is the pandabuy spreadsheet free to use?',
    answer: 'Yes, the pandabuy spreadsheet directory is completely free to browse. Simply choose a category from the dropdown menu or click any category card to explore product links. No registration or payment is required.',
  },
  {
    id: 'faq-4',
    question: 'How do I find the best pandabuy spreadsheet links?',
    answer: 'Start by selecting a category that interests you — Shoes, Hoodies, Bags, or any of the 14 categories available. Each category contains hand-picked links organized for easy browsing. Our featured section also highlights trending products each week.',
  },
  {
    id: 'faq-5',
    question: 'Are the sellers in the pandabuy spreadsheet verified?',
    answer: 'We prioritize links from sellers with strong community feedback and consistent track records. While we cannot guarantee every transaction, our team reviews seller reputations before including links in the spreadsheet.',
  },
  {
    id: 'faq-6',
    question: 'What categories does the pandabuy spreadsheet cover?',
    answer: 'Our pandabuy spreadsheet covers 14 categories: Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff — everything you need in one place.',
  },
  {
    id: 'faq-7',
    question: 'Can I request new categories for the pandabuy spreadsheet?',
    answer: 'We are always open to expanding our pandabuy spreadsheet. If there is a category you would like to see, feel free to reach out through our community channels. We regularly add new sections based on user demand.',
  },
  {
    id: 'faq-8',
    question: 'How is this pandabuy spreadsheet different from others?',
    answer: 'Unlike forum threads or outdated lists, our pandabuy spreadsheet is professionally organized, updated daily, and focused on quality. Every link is categorized and checked — saving you hours of searching and filtering through broken links.',
  },
];

export default function FAQSection() {
  const [openId, setOpenId] = useState<string | null>(null);

  const toggleFaq = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section id="faq" className="py-16 md:py-20 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 border border-foreground-200 rounded-full text-xs font-medium text-foreground-600 mb-4">
            FAQ
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
            Frequently Asked Questions
          </h2>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-3">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-white border border-background-200 rounded-lg overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => toggleFaq(faq.id)}
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
              >
                <h4 className="text-sm md:text-base font-semibold text-foreground-900 pr-4">
                  {faq.question}
                </h4>
                <i
                  className={`ri-arrow-down-s-line text-lg text-foreground-500 flex-shrink-0 transition-transform duration-200 ${
                    openId === faq.id ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              <div
                className={`px-5 overflow-hidden transition-all duration-300 ${
                  openId === faq.id ? 'pb-4 max-h-96' : 'max-h-0'
                }`}
              >
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </section>
  );
}